import { Component } from '@angular/core';

export type Lang = 'en' | 'es';

@Component({
  selector: 'app-package-page',
  templateUrl: './package-page.component.html',
  styleUrls: ['./package-page.component.css']
})
export class PackagePageComponent {

  isNavOpen = false;
  lang: Lang = 'en';

  setLang(l: Lang) {
    this.lang = l;
  }

  get t() {
    return this.translations[this.lang];
  }

  translations = {
    en: {
      nav: {
        login: 'LOGIN',
        register: 'REGISTER NOW',
      },
      hero: {
        tagline: 'FOR US. BY US. — POWERED BY UMYO.',
        title: 'CHOOSE YOUR PACKAGE.',
        titleHighlight: 'CONNECT. BUILD.',
        titleEnd: 'GROW.',
        desc: 'Affordable digital membership designed to help individuals, creators, professionals, entrepreneurs, and businesses build their presence, connect with the community, and be discovered.',
        cta: 'REGISTER NOW'
      },
      features: {
        one: {
          title: 'ONE COMMUNITY',
          subtitle: 'MILLIONS OF CONNECTIONS'
        },
        two: {
          title: 'BUILD YOUR BRAND',
          subtitle: 'GROW YOUR NETWORK, GET DISCOVERED'
        },
        three: {
          title: 'MORE VISIBILITY',
          subtitle: 'MORE OPPORTUNITIES, MORE IMPACT'
        }
      },
      memberPackages: {
        title: 'MEMBER PACKAGES',
        starter: {
          name: 'STARTER',
          price: '$2.54',
          desc: 'A simple way to establish your digital presence inside the UMYO Español community.',
          features: [
            'Personal Digital Profile',
            'Directory Listing',
            'Connect & Network',
            'Exclusive Community Access',
            'Updates & Announcements',
            '1 Image',
            '1 Digital Card',
            'Social Media Links',
            'QR Code'
          ],
          benefit: {
            label: 'DIRECTORY BENEFIT',
            title: 'STANDARD LISTING',
            desc: 'Be visible and searchable inside the directory.'
          },
          cta: 'SELECT PACKAGE'
        },
        connect: {
          name: 'CONNECT',
          price: '$5.99',
          desc: 'Build your presence, share more content, and let the community know you are open to opportunities.',
          features: [
            '3 Digital Cards',
            '2 Images',
            '1 Video',
            'Web Links',
            'Directory Access',
            'Community Networking'
          ],
          benefit: {
            label: 'DIRECTORY BENEFIT',
            title: 'AVAILABLE FOR HIRE',
            desc: 'Let potential clients, organizations, businesses, and collaborators know you are available for paid opportunities.'
          },
          cta: 'SELECT PACKAGE'
        },
        grow: {
          name: 'GROW',
          price: '$9.99',
          desc: 'More tools, more content, and more ways to present yourself inside the community.',
          features: [
            '6 Digital Cards',
            '3 Images',
            '2 Videos',
            'Web Links',
            'Directory Access',
            'Community Networking'
          ],
          benefit: {
            label: 'DIRECTORY BENEFIT',
            title: 'STANDARD LISTING',
            desc: 'Build a larger profile while remaining visible and searchable in the directory.'
          },
          cta: 'SELECT PACKAGE'
        },
        pro: {
          name: 'PRO',
          price: '$19.99',
          desc: 'Designed for members who want maximum visibility, credibility, and stronger placement inside the directory.',
          features: [
            '9 Digital Cards',
            'Unlimited Images',
            '3 Videos',
            'Web Links',
            'Directory Access',
            'Community Networking'
          ],
          benefits: [
            {
              label: 'DIRECTORY BENEFIT',
              title: 'VERIFIED MEMBER',
              desc: 'Receive a verified status badge that helps establish trust and credibility.'
            },
            {
              label: 'DIRECTORY BENEFIT',
              title: 'FEATURED MEMBER',
              desc: 'Receive placement within the Featured Members section of the directory for increased visibility.'
            }
          ],
          cta: 'SELECT PACKAGE'
        }
      },
      businessPackages: {
        title: 'BUSINESS PACKAGES',
        business: {
          name: 'BUSINESS',
          price: '$49.99',
          priceSuffix: '& UP',
          tag: 'ACCEPTING CLIENTS',
          desc: 'Put your business in front of the community and create more opportunities to connect with customers and clients.',
          features: [
            'Business Digital Profile',
            'Business Directory Listing',
            'Promote Products & Services',
            'Analytics & Services',
            '25 Digital Cards',
            '6 Videos',
            'Unlimited Images',
            'Social Media & Web Links',
            'QR Code'
          ],
          benefit: {
            label: 'DIRECTORY BENEFIT',
            title: 'ACCEPTING CLIENTS',
            desc: 'Let members know your business is currently accepting new customers, clients, appointments, or projects.'
          },
          cta: 'REGISTER YOUR BUSINESS'
        },
        businessPro: {
          name: 'BUSINESS PRO',
          price: '$109.99',
          tag: 'MAXIMUM VISIBILITY',
          badge: 'BEST VALUE',
          desc: 'Maximum visibility for businesses that want to stand out, attract new customers, promote special offers, and receive featured placement.',
          includes: 'EVERYTHING IN BUSINESS, PLUS:',
          features: [
            'Enhanced Business Presence',
            'Priority Directory Placement',
            'Custom Branding Opportunities',
            '25 Digital Cards',
            'Unlimited Images',
            '6 Videos',
            'Advanced Analytics',
            'Social Media & Web Links',
            'QR Code'
          ],
          benefits: [
            {
              title: 'ACCEPTING CLIENTS',
              desc: 'Show customers that your business is ready to serve them.'
            },
            {
              title: 'OFFERS DISCOUNTS',
              desc: 'Promote special discounts, offers, incentives, and savings directly to the community.'
            },
            {
              title: 'FEATURED BUSINESS',
              desc: 'Receive enhanced visibility and placement within the Featured Businesses section of the directory.'
            }
          ],
          cta: 'SELECT BUSINESS PRO'
        }
      },
      directoryAdvantages: {
        title: 'DIRECTORY ADVANTAGES',
        subtitle: 'Your package determines how your profile can appear and be discovered inside the UMYO Español Directory.',
        features: [
          { name: 'Standard Listing', packages: 'Starter & Grow' },
          { name: 'Available for Hire', packages: 'Connect' },
          { name: 'Verified Member', packages: 'Pro' },
          { name: 'Featured Member', packages: 'Pro' },
          { name: 'Accepting Clients', packages: 'Business' },
          { name: 'Offers Discounts', packages: 'Business Pro' },
          { name: 'Featured Business', packages: 'Business Pro' }
        ]
      },
      allInclude: {
        title: 'ALL PACKAGES INCLUDE',
        subtitle: 'Every package comes with powerful tools and support to help you grow in the UMYO Español ecosystem.',
        items: [
          {
            title: 'SECURE COMMUNITY ACCESS',
            desc: 'Connect inside a community-focused digital environment.'
          },
          {
            title: 'COMMUNITY NEWS & UPDATES',
            desc: 'Stay informed about opportunities, events, announcements, and new resources.'
          },
          {
            title: 'MEMBER SUPPORT',
            desc: 'Access support as you build your presence inside the ecosystem.'
          },
          {
            title: 'MOBILE-FRIENDLY EXPERIENCE',
            desc: 'Access your profile, directory, and community features across your devices.'
          }
        ]
      },
      ecosystem: {
        title: 'YOUR MEMBERSHIP HELPS BUILD THE ECOSYSTEM.',
        desc: 'Every registration helps strengthen the UMYO Español digital ecosystem, expand opportunities, support our community, and build technology designed for us.',
        quote: 'More connections. More opportunities. A stronger community.',
        ctaTitle: 'JOIN TODAY. CONNECT TODAY. GROW WITH US.',
        cta: 'CHOOSE YOUR PACKAGE'
      },
      footer: {
        tagline: 'FOR US. BY US. POWERED BY UMYO.',
        quickLinks: 'QUICK LINKS',
        resources: 'RESOURCES',
        connect: 'LET\'S CONNECT',
        copyright: '© 2026 UMYO Español Directory. All Rights Reserved.'
      }
    },
    es: {
      nav: {
        login: 'INICIAR SESIÓN',
        register: 'REGISTRARSE AHORA',
      },
      hero: {
        tagline: 'PARA NOSOTROS. POR NOSOTROS. — POTENCIADO POR UMYO.',
        title: 'ELIGE TU PAQUETE.',
        titleHighlight: 'CONECTA. CONSTRUYE.',
        titleEnd: 'CRECE.',
        desc: 'Membresía digital asequible diseñada para ayudar a individuos, creadores, profesionales, emprendedores y empresas a construir su presencia, conectar con la comunidad y ser descubiertos.',
        cta: 'REGISTRARSE AHORA'
      },
      features: {
        one: {
          title: 'UNA COMUNIDAD',
          subtitle: 'MILLONES DE CONEXIONES'
        },
        two: {
          title: 'CONSTRUYE TU MARCA',
          subtitle: 'CRECE TU RED, SÉ DESCUBIERTO'
        },
        three: {
          title: 'MÁS VISIBILIDAD',
          subtitle: 'MÁS OPORTUNIDADES, MÁS IMPACTO'
        }
      },
      memberPackages: {
        title: 'PAQUETES PARA MIEMBROS',
        starter: {
          name: 'INICIAL',
          price: '$2.54',
          desc: 'Una forma sencilla de establecer tu presencia digital dentro de la comunidad UMYO Español.',
          features: [
            'Perfil Digital Personal',
            'Listado en el Directorio',
            'Conecta y Haz Networking',
            'Acceso Exclusivo a la Comunidad',
            'Actualizaciones y Anuncios',
            '1 Imagen',
            '1 Tarjeta Digital',
            'Enlaces a Redes Sociales',
            'Código QR'
          ],
          benefit: {
            label: 'BENEFICIO DEL DIRECTORIO',
            title: 'LISTADO ESTÁNDAR',
            desc: 'Sé visible y búscable dentro del directorio.'
          },
          cta: 'SELECCIONAR PAQUETE'
        },
        connect: {
          name: 'CONECTA',
          price: '$5.99',
          desc: 'Construye tu presencia, comparte más contenido y hazle saber a la comunidad que estás abierto a oportunidades.',
          features: [
            '3 Tarjetas Digitales',
            '2 Imágenes',
            '1 Video',
            'Enlaces Web',
            'Acceso al Directorio',
            'Networking Comunitario'
          ],
          benefit: {
            label: 'BENEFICIO DEL DIRECTORIO',
            title: 'DISPONIBLE PARA CONTRATAR',
            desc: 'Hazle saber a clientes potenciales, organizaciones, negocios y colaboradores que estás disponible para oportunidades remuneradas.'
          },
          cta: 'SELECCIONAR PAQUETE'
        },
        grow: {
          name: 'CRECE',
          price: '$9.99',
          desc: 'Más herramientas, más contenido y más formas de presentarte dentro de la comunidad.',
          features: [
            '6 Tarjetas Digitales',
            '3 Imágenes',
            '2 Videos',
            'Enlaces Web',
            'Acceso al Directorio',
            'Networking Comunitario'
          ],
          benefit: {
            label: 'BENEFICIO DEL DIRECTORIO',
            title: 'LISTADO ESTÁNDAR',
            desc: 'Construye un perfil más amplio mientras permaneces visible y búscable en el directorio.'
          },
          cta: 'SELECCIONAR PAQUETE'
        },
        pro: {
          name: 'PRO',
          price: '$19.99',
          desc: 'Diseñado para miembros que desean máxima visibilidad, credibilidad y una posición más fuerte dentro del directorio.',
          features: [
            '9 Tarjetas Digitales',
            'Imágenes Ilimitadas',
            '3 Videos',
            'Enlaces Web',
            'Acceso al Directorio',
            'Networking Comunitario'
          ],
          benefits: [
            {
              label: 'BENEFICIO DEL DIRECTORIO',
              title: 'MIEMBRO VERIFICADO',
              desc: 'Recibe una insignia de estado verificado que ayuda a establecer confianza y credibilidad.'
            },
            {
              label: 'BENEFICIO DEL DIRECTORIO',
              title: 'MIEMBRO DESTACADO',
              desc: 'Recibe ubicación dentro de la sección de Miembros Destacados del directorio para mayor visibilidad.'
            }
          ],
          cta: 'SELECCIONAR PAQUETE'
        }
      },
      businessPackages: {
        title: 'PAQUETES PARA NEGOCIOS',
        business: {
          name: 'NEGOCIO',
          price: '$49.99',
          priceSuffix: 'Y MÁS',
          tag: 'ACEPTANDO CLIENTES',
          desc: 'Pon tu negocio frente a la comunidad y crea más oportunidades para conectar con clientes.',
          features: [
            'Perfil Digital de Negocio',
            'Listado en el Directorio de Negocios',
            'Promociona Productos y Servicios',
            'Analítica y Servicios',
            '25 Tarjetas Digitales',
            '6 Videos',
            'Imágenes Ilimitadas',
            'Enlaces a Redes Sociales y Web',
            'Código QR'
          ],
          benefit: {
            label: 'BENEFICIO DEL DIRECTORIO',
            title: 'ACEPTANDO CLIENTES',
            desc: 'Hazle saber a los miembros que tu negocio está actualmente aceptando nuevos clientes, citas o proyectos.'
          },
          cta: 'REGISTRA TU NEGOCIO'
        },
        businessPro: {
          name: 'NEGOCIO PRO',
          price: '$109.99',
          tag: 'MÁXIMA VISIBILIDAD',
          badge: 'MEJOR VALOR',
          desc: 'Máxima visibilidad para negocios que quieren destacar, atraer nuevos clientes, promover ofertas especiales y recibir ubicación destacada.',
          includes: 'TODO EN NEGOCIO, MÁS:',
          features: [
            'Presencia Empresarial Mejorada',
            'Ubicación Prioritaria en el Directorio',
            'Oportunidades de Marca Personalizada',
            '25 Tarjetas Digitales',
            'Imágenes Ilimitadas',
            '6 Videos',
            'Analítica Avanzada',
            'Enlaces a Redes Sociales y Web',
            'Código QR'
          ],
          benefits: [
            {
              title: 'ACEPTANDO CLIENTES',
              desc: 'Muestra a los clientes que tu negocio está listo para servirles.'
            },
            {
              title: 'OFREECE DESCUENTOS',
              desc: 'Promociona descuentos especiales, ofertas, incentivos y ahorros directamente a la comunidad.'
            },
            {
              title: 'NEGOCIO DESTACADO',
              desc: 'Recibe visibilidad mejorada y ubicación dentro de la sección de Negocios Destacados del directorio.'
            }
          ],
          cta: 'SELECCIONAR NEGOCIO PRO'
        }
      },
      directoryAdvantages: {
        title: 'VENTAJAS DEL DIRECTORIO',
        subtitle: 'Tu paquete determina cómo tu perfil puede aparecer y ser descubierto dentro del Directorio UMYO Español.',
        features: [
          { name: 'Listado Estándar', packages: 'Inicial y Crece' },
          { name: 'Disponible para Contratar', packages: 'Conecta' },
          { name: 'Miembro Verificado', packages: 'Pro' },
          { name: 'Miembro Destacado', packages: 'Pro' },
          { name: 'Aceptando Clientes', packages: 'Negocio' },
          { name: 'Ofrece Descuentos', packages: 'Negocio Pro' },
          { name: 'Negocio Destacado', packages: 'Negocio Pro' }
        ]
      },
      allInclude: {
        title: 'TODOS LOS PAQUETES INCLUYEN',
        subtitle: 'Cada paquete viene con herramientas poderosas y apoyo para ayudarte a crecer en el ecosistema UMYO Español.',
        items: [
          {
            title: 'ACCESO SEGURO A LA COMUNIDAD',
            desc: 'Conéctate dentro de un entorno digital enfocado en la comunidad.'
          },
          {
            title: 'NOTICIAS Y ACTUALIZACIONES DE LA COMUNIDAD',
            desc: 'Mantente informado sobre oportunidades, eventos, anuncios y nuevos recursos.'
          },
          {
            title: 'APOYO PARA MIEMBROS',
            desc: 'Accede a apoyo mientras construyes tu presencia dentro del ecosistema.'
          },
          {
            title: 'EXPERIENCIA COMPATIBLE CON MÓVILES',
            desc: 'Accede a tu perfil, directorio y funciones comunitarias en todos tus dispositivos.'
          }
        ]
      },
      ecosystem: {
        title: 'TU MEMBRESÍA AYUDA A CONSTRUIR EL ECOSISTEMA.',
        desc: 'Cada registro ayuda a fortalecer el ecosistema digital UMYO Español, expandir oportunidades, apoyar a nuestra comunidad y construir tecnología diseñada para nosotros.',
        quote: 'Más conexiones. Más oportunidades. Una comunidad más fuerte.',
        ctaTitle: 'ÚNETE HOY. CONECTA HOY. CRECE CON NOSOTROS.',
        cta: 'ELIGE TU PAQUETE'
      },
      footer: {
        tagline: 'PARA NOSOTROS. POR NOSOTROS. POTENCIADO POR UMYO.',
        quickLinks: 'ENLACES RÁPIDOS',
        resources: 'RECURSOS',
        connect: 'CONECTEMOS',
        copyright: '© 2026 Directorio UMYO Español. Todos los derechos reservados.'
      }
    }
  };
}